// Thin wrappers over the auth-service /login/* endpoints. All fetches set
// credentials:'include' so the HttpOnly passwordless_login_id cookie set
// on /request is sent back on /verify and /poll.
import {referralRequestBody} from "./referral.ts";
import {signupRequestBody, type SignupInput} from "../../shared/signup-profile.ts";

export interface RequestResult {
    login_id: string;
    expires_at: string;
    next_step: "code" | "code_and_signup";
    retry_after_seconds?: number;
    voice_available?: boolean;
}

export type PollStatus =
    | {status: "pending"}
    | {status: "expired"}
    | {status: "not_found"}
    | {status: "completed_existing_user"; access_token: string; refresh_token: string; expires_in: number; user_id: number}
    | {status: "completed_new_user"; email: string | null; login_id: string};

export type VerifyResult =
    | {status: "completed_existing_user"; access_token: string; refresh_token: string; expires_in: number; user_id: number}
    | {status: "completed_signup"; access_token: string; refresh_token: string; expires_in: number; user_id: number}
    | {status: "completed_new_user"; email: string | null; login_id: string};

export interface SignupResult {
    access_token: string;
    refresh_token: string;
    expires_in: number;
    user_id: number;
}

export class ApiError extends Error {
    constructor(public status: number, public code: string, public detail?: unknown) {
        super(code);
    }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
    const res = await fetch(path, {
        credentials: "include",
        headers: {"Content-Type": "application/json"},
        ...init,
    });
    if (!res.ok) {
        let body: unknown;
        try { body = await res.json(); } catch { body = null; }
        const code = isRecord(body) && typeof body.error === "string" ? body.error : "request_failed";
        throw new ApiError(res.status, code, body);
    }
    return await res.json() as T;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
}

export const requestEmailCode = (email: string) =>
    call<RequestResult>("/login/email/request", {method: "POST", body: JSON.stringify({email, ...referralRequestBody()})});

export const verifyEmailCode = (params: {code: string; loginId?: string; signup?: SignupInput}) =>
    call<VerifyResult>("/login/email/verify", {method: "POST", body: JSON.stringify({
        code: params.code,
        ...(params.loginId ? {login_id: params.loginId} : {}),
        ...(params.signup ? signupRequestBody(params.signup) : {}),
        ...referralRequestBody(),
    })});

export const pollLogin = (loginId: string) =>
    call<PollStatus>(`/login/poll?id=${encodeURIComponent(loginId)}`);

export const completeSignup = (params: SignupInput, loginId?: string) =>
    call<SignupResult>("/login/signup", {method: "POST", body: JSON.stringify({
        ...signupRequestBody(params),
        ...(loginId ? {login_id: loginId} : {}),
        ...referralRequestBody(),
    })});

export interface PendingOAuthProfile {
    provider: string;
    email: string | null;
    firstName: string;
    lastName: string;
    suggestedUserName: string;
    pictureUrl: string | null;
}
export const fetchPendingOAuth = (token: string) =>
    call<PendingOAuthProfile>(`/login/oauth/pending?token=${encodeURIComponent(token)}`);

export const completeOAuthSignup = (params: {token: string; signup: SignupInput}) =>
    call<SignupResult & {return_to: string}>("/login/oauth/complete-signup", {
        method: "POST",
        body: JSON.stringify({token: params.token, ...signupRequestBody(params.signup)}),
    });

export const linkPendingOAuth = (params: {token: string; accessToken: string}) =>
    call<{ok: true; return_to: string}>("/login/oauth/link-pending", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${params.accessToken}`,
        },
        body: JSON.stringify({token: params.token}),
    });

export const createNativeHandoff = (params: {returnTo: string; accessToken: string; refreshToken: string}) =>
    call<{redirect_to: string}>("/login/native/handoff", {
        method: "POST",
        body: JSON.stringify({
            return_to: params.returnTo,
            access_token: params.accessToken,
            refresh_token: params.refreshToken,
        }),
    });

// --- phone rail (Telnyx Verify on the server; 404 unless the deployment enables it) ---

/** delivery "call" = the page's "Call me": the first send is a call that reads the code out. */
export const requestSmsCode = (phone: string, delivery: "sms" | "call" = "sms", turnstile?: string) =>
    call<RequestResult & {fallback?: "call"}>("/login/sms/request", {method: "POST", body: JSON.stringify({phone, ...(delivery === "call" ? {delivery} : {}), ...(turnstile ? {turnstile} : {}), ...referralRequestBody()})});

export const callSmsCode = (phone: string, loginId: string) =>
    call<{status: "calling"; retry_after_seconds: number}>("/login/sms/call", {method: "POST", body: JSON.stringify({phone, login_id: loginId})});

export const verifySmsCode = (params: {loginId: string; phone: string; code: string; signup?: SignupInput}) =>
    call<VerifyResult>("/login/sms/verify", {method: "POST", body: JSON.stringify({
        login_id: params.loginId,
        phone: params.phone,
        code: params.code,
        ...(params.signup ? signupRequestBody(params.signup) : {}),
        ...referralRequestBody(),
    })});

// --- signed-in phone verification (the auth service's /login/phone page, SSO-cookie session) ---

const PHONE_PAGE = {"Content-Type": "application/json", "X-Auth-Phone-Page": "1"};

export const startAccountPhone = (phone: string, turnstile?: string) =>
    call<{verification_id: string; expires_in: number; retry_after_seconds: number; voice_available:boolean}>("/user/phone/start", {method: "POST", headers: PHONE_PAGE, body: JSON.stringify({phone, ...(turnstile ? {turnstile} : {})})});

export const callAccountPhone = (verificationId: string) =>
    call<{status: "calling"; retry_after_seconds: number}>("/user/phone/call", {method: "POST", headers: PHONE_PAGE, body: JSON.stringify({verification_id: verificationId})});

export const verifyAccountPhone = (verificationId: string, code: string) =>
    call<{status: "verified"; phone_number: string; phone_country: string | null}>("/user/phone/verify", {method: "POST", headers: PHONE_PAGE, body: JSON.stringify({verification_id: verificationId, code})});

export const resendLoginCode = (loginId: string) => call<{status: "requested"; retry_after_seconds: number}>("/login/delivery/resend", {method:"POST",body:JSON.stringify({login_id:loginId})});

export const resendAccountPhone = (verificationId:string) => call<{status:"requested";retry_after_seconds:number}>("/user/phone/resend",{method:"POST",headers:PHONE_PAGE,body:JSON.stringify({verification_id:verificationId})});
